import { useApolloClient } from '@apollo/react-hooks'
import { gql } from 'apollo-boost'
import type { ApolloError } from 'apollo-client'
import { useSnackbar } from 'notistack'
import * as Sentry from '@sentry/react'
import { getPostHogClientContext } from 'shared/util/analytics'
import { LANGUAGE_ENGLISH_NAMES } from '@paintscout/ui'
import type { SupportedLang } from '@paintscout/ui'
import type { TranslationCompletionStatus } from '@paintscout/util/builder/quote/util/translation-status'
import type { QuoteDocument } from 'paintscout'

// Pre-bakes quote.translations[lang] server-side and returns the per-language completion status
// plus the already-persisted quote, so the freshly pre-baked content can be downloaded straight
// from the mutation response — no separate quote(id) round trip for the same document.
const REFRESH_QUOTE_TRANSLATIONS = gql`
  mutation RefreshQuoteTranslations(
    $quoteId: String!
    $companyId: String!
    $trigger: String
    $posthogDistinctId: String
    $posthogSessionId: String
  ) {
    refreshQuoteTranslations(
      quoteId: $quoteId
      companyId: $companyId
      trigger: $trigger
      posthogDistinctId: $posthogDistinctId
      posthogSessionId: $posthogSessionId
    ) {
      success
      translationStatus {
        lang
        status
      }
      quote
    }
  }
`

export type LangStatusEntry = { lang: string; status: TranslationCompletionStatus }

// Merge the per-language statuses a refresh just returned into the existing status map, keyed
// by lang. Entries can be null over the wire (nullable list item), hence the type guard rather
// than `.filter(Boolean)` (which doesn't narrow past `| null` on its own).
export function mergeLangStatus(
  current: Record<string, TranslationCompletionStatus>,
  entries: Array<LangStatusEntry | null | undefined>
): Record<string, TranslationCompletionStatus> {
  const merged = { ...current }
  for (const entry of entries) {
    if (entry) merged[entry.lang] = entry.status
  }
  return merged
}

export interface GenerateWorkOrderTranslationParams {
  quote: QuoteDocument
  companyId: string
  lang: string
  /** Allowlisted server-side (refreshQuoteTranslations.ts CLIENT_TRIGGERS) — attributes the
   * generation event to whichever surface triggered it (ENG-766). */
  trigger: string
  /** Names the calling component for Sentry breadcrumbs/tags. */
  source: string
}

export interface GenerateWorkOrderTranslationResult {
  success: boolean
  quote: QuoteDocument | null
  translationStatus: Array<LangStatusEntry | null>
}

// Shared by DownloadWorkOrderDialog and WorkOrderViewDialog so both surfaces generate a
// translation (and report/log failures) the exact same way (#22507).
export function useGenerateWorkOrderTranslation() {
  const apolloClient = useApolloClient()
  const { enqueueSnackbar } = useSnackbar()

  return async function generateWorkOrderTranslation({
    quote,
    companyId,
    lang,
    trigger,
    source
  }: GenerateWorkOrderTranslationParams): Promise<GenerateWorkOrderTranslationResult> {
    const langLabel = LANGUAGE_ENGLISH_NAMES[lang as SupportedLang] ?? lang

    // Fail fast without a network call (and without incurring a wasted Lambda invocation) if the
    // caller was ever mounted without a companyId — the resolver would reject it anyway, but
    // there's no reason to pay for the round trip to find that out.
    if (!companyId) {
      Sentry.captureMessage(`${source} rendered without a companyId`, {
        tags: { source, lang, quoteId: quote._id }
      })
      enqueueSnackbar(`Unable to generate ${langLabel} translation. Please try again.`, { variant: 'error' })
      return { success: false, quote: null, translationStatus: [] }
    }

    try {
      // Read at call time, not render time: the session id can roll over (30 min idle) while
      // generation is in flight, and a stale one would file the event under a replay that
      // already ended.
      const postHogContext = getPostHogClientContext()

      const { data } = await apolloClient.mutate<{
        refreshQuoteTranslations?: {
          success: boolean
          translationStatus?: Array<LangStatusEntry | null> | null
          quote?: QuoteDocument | null
        } | null
      }>({
        mutation: REFRESH_QUOTE_TRANSLATIONS,
        variables: {
          quoteId: quote._id,
          companyId,
          trigger,
          posthogDistinctId: postHogContext.distinctId,
          posthogSessionId: postHogContext.sessionId
        }
      })

      const result = data?.refreshQuoteTranslations
      const nextStatus = result?.translationStatus ?? []

      // The mutation can resolve without throwing yet still not have produced the translation
      // (AI call failed/timed out and was swallowed server-side). Trust the returned status.
      // Note: `result.success` means *every* enabled language reached "complete" (it also
      // retries languages other than the one selected here) — that's the right semantic for
      // the admin bulk-retry action, but checking it here would block (or, worse, silently skip
      // re-generation on a later click — see below) this specific language's download just
      // because some other, unrelated language failed. Only this language's own status matters.
      const langStatus = nextStatus.find((entry) => entry?.lang === lang)?.status
      if (langStatus !== 'complete' || !result?.quote) {
        // No exception was thrown here — the mutation itself resolved fine, the AI generation
        // just didn't complete (or the persisted quote wasn't returned) server-side — so capture
        // a breadcrumb explicitly rather than relying on the catch block below, which never sees
        // this case (#21747).
        Sentry.captureMessage('refreshQuoteTranslations resolved without completing the translation', {
          tags: { source, lang, quoteId: quote._id, companyId }
        })
        enqueueSnackbar(`Unable to generate ${langLabel} translation. Please try again.`, { variant: 'error' })
        return { success: false, quote: null, translationStatus: nextStatus }
      }

      // The mutation already returns the persisted quote with translations applied, so there's
      // no need for a second quote(id) fetch just to see the same document.
      // The refreshQuoteTranslations mutation reports work_order_translation_generated
      // server-side, where the content is actually generated, so every path is counted once
      // (see trackTranslationsGenerated — ENG-766). Tracking it here as well would double-count.
      return { success: true, quote: result.quote, translationStatus: nextStatus }
    } catch (err) {
      Sentry.captureException(err, { tags: { source, lang } })
      const code = (err as ApolloError)?.graphQLErrors?.[0]?.extensions?.code
      enqueueSnackbar(
        code === 'TRANSLATION_UNAVAILABLE'
          ? 'Translation is not available right now. Please contact support.'
          : code === 'RATE_LIMITED'
            ? 'Translation is already being generated for this quote. Please wait a moment and try again.'
            : 'Unable to generate translation. Please try again.',
        { variant: 'error' }
      )
      return { success: false, quote: null, translationStatus: [] }
    }
  }
}
